import React, { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions, View, ScrollView, Platform, StyleSheet } from 'react-native';
import Label, { LabelType } from '../../common/Label';
import useTranslation from '../../../hooks/translation-hook';
import { LinearGradient } from 'expo-linear-gradient';
import MenuItem from './MenuItem';
import MyAccountNavigationButton from '../MyAccountNavigationButton';
import useStyles from '../../../hooks/styles-hook';
import NavigationElements from '../../../models/NavigationElements';
import useMyAccountActions from '../../../hooks/myAccountActions-hook';
import {
  faArrowRightArrowLeft,
  faArrowRightFromBracket,
  faAward,
  faBadgeCheck,
  faCircleQuestion,
  faGear,
  faGlobePointer,
  faIdCard,
  faSealExclamation,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { GetMyRewardsViewModel, MyRewardsClient } from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import { LoggedState } from '../../../models/AuthModelsCommon';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import MoreInfoButton from '../../common/MoreInfoButton';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { QualityWarningAvatarBadge } from '../../common/qualityWarning/QualityWarningIndicator';
import MyRewardsCard from './MyRewardsCard';
import { useProfileCompletionBonus } from '../../../hooks/useProfileCompletionBonus';

const MY_REWARDS_REFRESH_INTERVAL_MS = 30_000;

const Menu = () => {
  const { hasOffer: hasProfileBonus, summary: profileBonus, observe: observeProfileBonus } = useProfileCompletionBonus();
  useFocusEffect(useCallback(() => observeProfileBonus(), [observeProfileBonus]));
  const t = useTranslation();

  const styles = useStyles((section) => section.myAccount);
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const panelTopInset = Math.max(insets.top, Platform.OS === 'android' ? 30 : 0);
  const myAccountActions = useMyAccountActions();
  const navigation = useNavigation();
  const authStore = useAppSelector((selector) => selector.auth);
  const accountStore = useAppSelector((selector) => selector.myAccount);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const apiRewards = useApi(MyRewardsClient);

  const [rewards, setRewards] = useState<GetMyRewardsViewModel>();
  const [userInitials, setUserInitials] = useState('');

  const dimensions = useWindowDimensions();

  const getMyRewards = useQuery({
    enabled: !!authStore.idToken,
    queryKey: ['myRewardsQuery', authStore.logged, authStore.idToken, accountStore.myRewardsSeed],
    refetchInterval: MY_REWARDS_REFRESH_INTERVAL_MS,
    queryFn: ({ signal }) => {
      const promise = apiRewards.getMyRewards(apiRewards.tokenSource?.token);

      // Cancel the request if React Query signals to abort
      signal?.addEventListener('abort', () => {
        apiRewards.tokenSource?.cancel('Query was cancelled by React Query');
        apiRewards.onCancel();
      });

      return promise;
    },
    onError: (err) => {
      throw err;
    },
  });

  useEffect(() => {
    setRewards(getMyRewards.data);
  }, [getMyRewards.isSuccess, getMyRewards.data]);

  useEffect(() => {
    if (userPrefs) {
      const initials = `${userPrefs.firstName ? userPrefs.firstName.substring(0, 1) : '-'}${userPrefs.lastName ? userPrefs.lastName.substring(0, 1) : '-'}`;
      setUserInitials(initials);
    }
  }, [userPrefs.firstName, userPrefs.lastName]);

  const handleMyRewardsClick = () => {
    navigation.navigate(NavigationElements.myRewards as never);
  };

  return (
    <View style={[styles.menu.container, layout.width > 1200 ? { width: 375, right: 0, left: 'auto' } : {}]}>
      {/* Extend the background into the safe-area insets while keeping content layout unchanged. */}
      <LinearGradient
        colors={['#035164', '#43a49e']}
        end={{ x: 1.4, y: 0 }}
        style={[StyleSheet.absoluteFillObject, { top: -insets.top, bottom: -insets.bottom }]}
      />
      <View style={[{ flex: 1 }, { ...StylesHelper.padding(16) }, layout.width > 1200 ? {} : { paddingTop: 16 + panelTopInset }]}>
        <ScrollView contentContainerStyle={{ minHeight: '100%', paddingBottom: Math.max(insets.bottom, 0) + 12 }}>
          <View style={{ flexGrow: 1 }}>
            <View>
              <MyAccountNavigationButton
                title={t('back')}
                navigationTitleColor={'white'}
                iconColor={'white'}
                onPress={() => myAccountActions.setVisibility(false)}
              />
            </View>
            <View style={styles.loggedInfo.container}>
              <View style={styles.loggedInfo.initials}>
                <QualityWarningAvatarBadge />
                {authStore.logged !== LoggedState.Anonymous && userInitials !== '--' && (
                  <Label numberOfLines={1} variant={LabelType.titleLargeNoCaps} style={styles.loggedInfo.initialsLabel}>
                    {userInitials}
                  </Label>
                )}
                {(authStore.logged === LoggedState.Anonymous || userInitials === '--') && (
                  <FontAwesomeIcon icon={faUser} style={{ color: 'white' }} size={24} />
                )}
              </View>
              <View style={[styles.loggedInfo.info]}>
                {authStore.user?.email && (
                  <>
                    {userPrefs?.firstName && userPrefs?.lastName && (
                      <Label numberOfLines={1} variant={LabelType.titleLargeNoCaps} style={styles.loggedInfo.initialsLabel}>
                        {`${userPrefs.firstName} ${userPrefs.lastName}`}
                      </Label>
                    )}
                    <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <View style={{ flexShrink: 1 }}>
                        <Label numberOfLines={1} variant={LabelType.titleLargeNoCaps} style={styles.loggedInfo.initialsLabelEmail} ellipsizeMode={'tail'}>
                          {`${authStore.user?.email}`}
                        </Label>
                      </View>
                      {authStore.user.emailVerified ? (
                        <MoreInfoButton style={{ marginLeft: 4 }} icon={<FontAwesomeIcon icon={faBadgeCheck} color="white" size={20} />}>
                          {t('Email-verified-Your-email-has-been-verified-which')}
                        </MoreInfoButton>
                      ) : (
                        <MoreInfoButton style={{ marginLeft: 4 }} icon={<FontAwesomeIcon icon={faSealExclamation} color="white" size={20} />}>
                          {t('Email-unverified-Your-email-is-currently-unverifie')}
                        </MoreInfoButton>
                      )}
                    </View>
                  </>
                )}
                {!authStore.user?.email && (
                  <Label numberOfLines={1} variant={LabelType.titleLargeNoCaps} style={styles.loggedInfo.initialsLabelEmail}>
                    {t(`anonymous`)}
                  </Label>
                )}
              </View>
            </View>
            <View style={{ flexDirection: 'row' }}>
              <Label style={styles.menu.myProfileTitle} variant={LabelType.titleLargeNoCaps}>
                {t('my_profile')}
              </Label>
              <MoreInfoButton style={{ marginLeft: 8 }} title={''} color={'white'} openURL={'my-profile-lanla-website-suuport-link'}>
                {t('help.my-profile')}
              </MoreInfoButton>
            </View>
            {authStore.logged !== LoggedState.Anonymous && (
              <MyRewardsCard rewards={rewards} onPress={handleMyRewardsClick} />
            )}
            {/* Menu */}
            <View style={styles.menu.menuList}>
              {authStore.logged !== LoggedState.Anonymous && (
                <MenuItem bottomLine icon={faAward} title={t('my_rewards')} action={NavigationElements.myRewards} />
              )}
              {authStore.logged !== LoggedState.Anonymous && (
                <MenuItem bottomLine icon={faUser} title={t('my_profile_information')} action={NavigationElements.myProfile}
                  trailingLabel={hasProfileBonus ? t('profile-bonus-badge', { points: profileBonus?.rewardPoints }) : undefined}
                  forwardProps={hasProfileBonus ? { profileBonusFocus: Date.now() } : undefined} />
              )}
              {authStore.logged !== LoggedState.Anonymous && (
                <MenuItem bottomLine icon={faIdCard} title={t('id-verification')} action={NavigationElements.idVerification} />
              )}
              {authStore.logged !== LoggedState.Anonymous && (
                <MenuItem bottomLine icon={faArrowRightArrowLeft} title={t('my_transactions')} action={NavigationElements.myTransactions} />
              )}
              <MenuItem icon={faGear} title={t('settings')} action={NavigationElements.settings} />
            </View>
            {/* Bottom items */}
            <View>
              {/* <MenuItem bottomLine icon={faBug} title={'Test 401'} action={NavigationElements.test401} /> */}
              <MenuItem bottomLine icon={faCircleQuestion} title={t('help_support')} externalLink={t('lanla_website_support')} />
              <MenuItem bottomLine icon={faGlobePointer} title={t('lanla-website')} externalLink={`${t('lanla_website_url')}`} />
              <View style={styles.menu.logoutButton}>
                <MenuItem
                  icon={faArrowRightFromBracket}
                  mirrorIcon
                  title={authStore.logged === LoggedState.Anonymous ? t('login_title') : t('logout')}
                  action={null}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

export default Menu;
